import React from 'react';
import { motion } from 'framer-motion';

import FadeIn from '../../../components/animations/FadeIn';

// "From address to roof — 4 steps." homepage section (2026-05-23).
// Replaces the prior ProcessPreview ("Same Quality, Better Process") in
// the homepage layout. ProcessPreview.jsx stays on disk per the repo
// convention — currently no other importers, available if its richer
// content (Interactive Demo CTA, benefits grid) is wanted on a different
// page later.
//
// Pass 1 styling layered on top of the original structure (2026-05-23):
//   • Card depth + restrained hover lift adopt the same vocabulary the
//     gallery + founders grid use — inset cyan ring at 12% alpha, soft
//     slate elevation; hover intensifies the ring to 35%, lifts 4px,
//     emits a cyan glow.
//   • Numbered badges become premium focal anchors: cyan gradient
//     (var(--blue-glow) → #07B0E9 → var(--blue-deep)) matching the Trust
//     Bridge stat numbers + Zero Section $0 graphic, with a soft cyan halo
//     and a thin inset white top-highlight for dimensionality.
//
// Pass 2 — coupled line-and-card scroll-in choreography (2026-05-23):
//   • A connecting cyan flow line draws between cards in sequence.
//     Each line's END is synchronized with the NEXT card's START — the
//     line literally "delivers" the next step.
//   • Layout: 7-item grid (4 cards interleaved with 3 line wrappers).
//     Mobile (1-col): cards stack vertically, lines run top-to-bottom.
//     sm  (2-col):    line wrappers hide via `sm:hidden` → grid sees 4
//                     cards only → clean 2×2 with no broken cascade.
//     lg  (4-col):    grid template is `1fr_auto_1fr_auto_1fr_auto_1fr`
//                     so lines occupy auto-width columns between cards.
//   • Timing — SEQUENCE_STEP 0.45s per beat; cards 0.40s fade-up; lines
//     0.20s scaleX/scaleY from anchored origin (left horizontal, top
//     vertical). Total reveal ~1.75s, snappy.
//   • Fires once on scroll-in via `viewport={{ once: true }}` on the
//     orchestrating parent motion.div.
//
// Trademark + product-name positioning in step 2 is per Mike's content-
// strategy call (2026-05-23): SmartPick™ AI / RoofMatch™ Technology /
// Design Studio are shipped as-written in the mockup. Other site pages
// currently position these trademarks differently and will be reconciled
// in a separate pass.
//
// The Pass 1 CSS .step-card pseudo-element flow line in tailwind.css is
// now ORPHAN — superseded by Pass 2's animated DOM elements. Left in place
// as a safe fallback if the animated version ever needs to be reverted;
// can be removed in a cleanup pass if Pass 2 sticks.

// Card depth vocabulary — same constants used by RecentInstallsSection +
// WhyWeBuiltSection's founders grid so the visual language stays coherent
// across the page.
const CARD_REST_SHADOW =
  'inset 0 0 0 1px rgba(7, 176, 233, 0.12),' +
  ' 0 1px 2px rgba(15, 23, 42, 0.05),' +
  ' 0 10px 28px rgba(15, 23, 42, 0.07)';

const CARD_HOVER_SHADOW =
  'inset 0 0 0 1px rgba(7, 176, 233, 0.35),' +
  ' 0 2px 6px rgba(15, 23, 42, 0.07),' +
  ' 0 4px 12px rgba(15, 23, 42, 0.10),' +
  ' 0 20px 50px rgba(7, 176, 233, 0.18)';

const CARD_EASE = [0.16, 1, 0.3, 1];
const ENTRANCE_EASE = [0.25, 0.4, 0.25, 1]; // matches FadeIn's curve

// Sequence timing. Each "beat" = (card_duration + small_overlap). The
// line's draw duration sits at the END of each beat so the line's
// finish-time == next-card's start-time.
const SEQUENCE_STEP = 0.45;
const CARD_DURATION = 0.40;
const LINE_DURATION = 0.20;

// Empty wrapper variants — present so motion's state propagation has
// named states to pass through to descendants. No own animation.
const sequenceContainerVariants = {
  hidden: {},
  visible: {},
};

const cardEntranceVariants = {
  hidden: { opacity: 0, y: 16, boxShadow: CARD_REST_SHADOW },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    boxShadow: CARD_REST_SHADOW,
    transition: {
      delay: i * SEQUENCE_STEP,
      duration: CARD_DURATION,
      ease: ENTRANCE_EASE,
    },
  }),
  hover: {
    y: -4,
    boxShadow: CARD_HOVER_SHADOW,
    transition: { duration: 0.35, ease: CARD_EASE },
  },
};

// Line variants — one set per orientation. Each line's draw ENDS exactly
// when the next card's reveal STARTS, so the line visually "delivers"
// the next step.
// Line i sits between card i and card i+1. Card i+1's start =
// (i+1) * SEQUENCE_STEP. Line i should end at that moment, which means
// it starts at  (i+1) * SEQUENCE_STEP − LINE_DURATION.
const lineStartDelay = (i) => (i + 1) * SEQUENCE_STEP - LINE_DURATION;

const lineVariantsVertical = {
  hidden: { scaleY: 0 },
  visible: (i) => ({
    scaleY: 1,
    transition: {
      delay: lineStartDelay(i),
      duration: LINE_DURATION,
      ease: ENTRANCE_EASE,
    },
  }),
};

const lineVariantsHorizontal = {
  hidden: { scaleX: 0 },
  visible: (i) => ({
    scaleX: 1,
    transition: {
      delay: lineStartDelay(i),
      duration: LINE_DURATION,
      ease: ENTRANCE_EASE,
    },
  }),
};

const steps = [
  {
    number: '1',
    title: 'Enter your address',
    body: "AI-powered satellite measurement pulls your roof's exact dimensions in seconds.",
    time: '~30 minutes',
  },
  {
    number: '2',
    title: 'Pick your roof',
    body: 'SmartPick™ AI matches a shingle to your home using RoofMatch™ Technology — or browse the full catalog from GAF, Owens Corning, Atlas, and TAMKO in Design Studio.',
    time: '2-5 minutes',
  },
  {
    number: '3',
    title: 'Get your quote',
    body: 'Line-itemed price texted and emailed to you. Real materials, real labor, real warranty.',
    time: '~30 minutes',
  },
  {
    number: '4',
    title: 'We install',
    body: "Sign online when you're ready. Manufacturer-certified crews install in 1-2 weeks. Pay only when done.",
    time: '1-2 weeks',
  },
];

// ─── Sub-components ─────────────────────────────────────────────────────

const StepCard = ({ step, index }) => (
  <motion.article
    variants={cardEntranceVariants}
    custom={index}
    whileHover="hover"
    className="bg-white rounded-2xl p-6 lg:p-7 flex flex-col items-center text-center"
  >
    {/* Numbered cyan disc — premium focal anchor.
        Cyan vertical gradient mirrors the Trust Bridge stat numbers
        + Zero Section $0 graphic so the badge reads as part of the
        brand vocabulary. Inset white highlight at the top gives
        the disc dimensionality; outer cyan halo grounds it. */}
    <div
      className="w-14 h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center text-white font-black text-2xl lg:text-[28px] mb-5 flex-shrink-0"
      style={{
        background:
          'linear-gradient(180deg, var(--blue-glow) 0%, #07B0E9 50%, var(--blue-deep) 100%)',
        boxShadow:
          'inset 0 1px 0 rgba(255, 255, 255, 0.30),' +
          ' 0 8px 24px rgba(7, 176, 233, 0.40)',
      }}
      aria-hidden="true"
    >
      {step.number}
    </div>

    <h3
      className="text-lg lg:text-xl font-bold mb-3 leading-snug"
      style={{ color: '#000150' }}
    >
      {step.title}
    </h3>

    <p className="text-sm lg:text-[15px] text-gray-600 leading-relaxed mb-5 flex-1">
      {step.body}
    </p>

    {/* Time pill — unchanged from earlier passes. */}
    <span
      className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
      style={{
        background: 'rgba(16, 185, 129, 0.12)',
        color: '#047857',
      }}
    >
      {step.time}
    </span>
  </motion.article>
);

// FlowLine renders two motion elements gated by viewport — one vertical for
// mobile, one horizontal for desktop. The whole wrapper is hidden at the sm
// breakpoint (`sm:hidden`) so the 2×2 tablet grid sees only the 4 cards.
const FlowLine = ({ index }) => (
  <div
    aria-hidden="true"
    className="flex items-center justify-center sm:hidden lg:flex lg:items-start lg:justify-start lg:pt-[60px] lg:px-2"
  >
    {/* Mobile: vertical line, 2×12 px, scaleY anchored at top so it draws
        downward toward the next stacked card. */}
    <motion.span
      variants={lineVariantsVertical}
      custom={index}
      className="block lg:hidden"
      style={{
        width: 2,
        height: 12,
        background:
          'linear-gradient(180deg, rgba(7, 176, 233, 0.30), rgba(7, 176, 233, 0.85), rgba(7, 176, 233, 0.30))',
        borderRadius: 2,
        transformOrigin: 'top',
      }}
    />
    {/* Desktop: horizontal line, 32×2 px, scaleX anchored at left so it
        draws rightward toward the next card in the row. */}
    <motion.span
      variants={lineVariantsHorizontal}
      custom={index}
      className="hidden lg:block"
      style={{
        width: 32,
        height: 2,
        background:
          'linear-gradient(90deg, rgba(7, 176, 233, 0.30), rgba(7, 176, 233, 0.85), rgba(7, 176, 233, 0.30))',
        borderRadius: 2,
        transformOrigin: 'left',
      }}
    />
  </div>
);

// ─── Section ────────────────────────────────────────────────────────────

const HowItWorksSection = () => (
  <section className="bg-white py-16 lg:py-24">
    <div className="max-w-[1200px] mx-auto px-5 lg:px-8">

      {/* A. Section header — FadeIn per line, near-simultaneous trigger
            since the three elements stack tight. Independent observer
            from the 4-step sequence below. */}
      <div className="text-center mb-12 lg:mb-16">
        <FadeIn
          as="p"
          delay={0}
          duration={0.4}
          className="text-xs font-bold uppercase tracking-[0.2em] mb-4"
          style={{ color: '#07B0E9' }}
        >
          How it works
        </FadeIn>
        <FadeIn
          as="h2"
          delay={0.08}
          duration={0.5}
          className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 leading-[1.1] tracking-tight"
          style={{
            color: '#000150',
            fontFamily: "'Poppins', 'Montserrat', sans-serif",
            letterSpacing: '-0.02em',
          }}
        >
          From address to roof — <span style={{ color: '#07B0E9' }}>4 steps.</span>
        </FadeIn>
        <FadeIn
          as="p"
          delay={0.18}
          duration={0.45}
          className="text-gray-500 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed"
        >
          No kitchen-table sales visits. No phone-tag. Most homeowners get their quote in ~30 minutes — followed by one low-pressure call from a real roofer (never a salesperson) if you have questions.
        </FadeIn>
      </div>

      {/* B. 4-step sequence — single observer drives the orchestrated
            line + card reveal. Once-only. */}
      <motion.div
        variants={sequenceContainerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] gap-2 sm:gap-4 lg:gap-0 items-start"
      >
        <StepCard step={steps[0]} index={0} />
        <FlowLine index={0} />
        <StepCard step={steps[1]} index={1} />
        <FlowLine index={1} />
        <StepCard step={steps[2]} index={2} />
        <FlowLine index={2} />
        <StepCard step={steps[3]} index={3} />
      </motion.div>

    </div>
  </section>
);

export default HowItWorksSection;
